import React, { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Button } from "primereact/button";
import InputText from "../../../../components/form-control/input-text";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from 'primereact/card';
import { Skeleton } from "primereact/skeleton";
import apiFunctions from "../../../../services/api-service";
import useToast from "../../../../components/common/useToast";
import { AxiosError } from "axios";
import { errorHandler } from "../../../../utils/HelperFunctions";
import { persistor } from "../../../../redux/store";
import { logout } from "../../../../redux/features/authSlice";
import { useDispatch } from "react-redux";
import { ArrowLeftIcon, BuildingLibraryIcon } from "@heroicons/react/24/outline";

const GovtDepartmentCreate: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { showToast }: any = useToast();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(!!id);
    const [submitting, setSubmitting] = useState(false);

    const [iconFile, setIconFile] = useState<File | null>(null);
    const [iconPreview, setIconPreview] = useState<string | null>(null);
    const iconInputRef = useRef<HTMLInputElement | null>(null);

    const validationSchema = Yup.object().shape({
        name: Yup.string().required("Name is required!"),
    });

    const initialValue = {
        name: "",
        namegujarati: ""
    };

    const formik = useFormik({
        initialValues: initialValue,
        validationSchema: validationSchema,

        onSubmit: async (values: any) => {
            setSubmitting(true);
            const formData = new FormData();
            formData.append("name", values.name);
            formData.append("namegujarati", values.namegujarati);
            if (iconFile) formData.append("icon", iconFile);

            if (id) {
                await apiFunctions.put(`/govtdepartment/editgovtdepartment/${id}`, formData, undefined, true)
                    .then((response) => {
                        if (response && response.data && response.data.success) {
                            showToast({
                                severity: "success",
                                summary: "Success",
                                detail: response.data.message,
                                life: 3000,
                            });
                        } else {
                            showToast({
                                severity: "error",
                                summary: "Error",
                                detail: response.data.message,
                                life: 3000,
                            });
                        }
                    })
                    .catch((err: AxiosError) => {
                        if (err.status === 401) {
                            dispatch(logout());
                            persistor.purge();
                            navigate("/admin/login");
                        }
                        errorHandler(err);
                    })
                    .finally(() => {
                        setSubmitting(false);
                        navigate("/admin/govtdepartment");
                    });
            } else {
                await apiFunctions.post(`/govtdepartment/addgovtdepartment`, formData, null, true)
                    .then((response) => {
                        if (response && response.data && response.data.success) {
                            showToast({
                                severity: "success",
                                summary: "Success",
                                detail: response.data.message,
                                life: 3000,
                            });
                        } else {
                            showToast({
                                severity: "error",
                                summary: "Error",
                                detail: response.data.message,
                                life: 3000,
                            });
                        }
                    })
                    .catch((err: AxiosError) => {
                        if (err.status === 401) {
                            dispatch(logout());
                            persistor.purge();
                            navigate("/admin/login");
                        }
                        errorHandler(err);
                    })
                    .finally(() => {
                        setSubmitting(false);
                        navigate("/admin/govtdepartment");
                    });
            }
        },
    });

    const handleIconChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setIconFile(file);
            setIconPreview(URL.createObjectURL(file));
        }
    };

    const getDepartmentData = async (id: string) => {
        try {
            let url = `/govtdepartment/${id}`;
            await apiFunctions
                .get(url)
                .then((response) => {
                    if (response && response.data && response.data.success) {
                        formik.setFieldValue("name", response.data.data.name);
                        formik.setFieldValue("namegujarati", response.data.data.namegujarati || "");
                        if (response.data.data.iconUrl) {
                            setIconPreview(response.data.data.iconUrl);
                        }
                    } else {
                        showToast({
                            severity: "error",
                            summary: "Error",
                            detail: response.data.message,
                            life: 3000,
                        });
                    }
                })
                .catch((err: AxiosError) => {
                    errorHandler(err);
                })
                .finally(() => {
                    setLoading(false);
                });
        } catch (error) {
            console.log(error);
        }
    }

    useEffect(() => {
        if (id) {
            getDepartmentData(id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleBack = () => {
        navigate('/admin/govtdepartment');
    }

    return (
        <>
            <div className="back-button" onClick={handleBack}>
                <ArrowLeftIcon className="w-6 h-6 text-black-700" /><h3 className="m-0">Back</h3>
            </div>
            <div className="page-title">
                {id ? (<h3>Edit Government Department</h3>) : (<h3>Add Government Department</h3>)}
            </div>
            <form action="submit" onSubmit={formik.handleSubmit}>
                <Card className="grid form-layout">
                    {loading ? (
                        <>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-12 md:col-span-6">
                                    <Skeleton width="80px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="42px" />
                                </div>
                                <div className="col-span-12 md:col-span-6">
                                    <Skeleton width="120px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="42px" />
                                </div>
                            </div>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="field mt-5 mb-4">
                                    <Skeleton width="90px" height="40px" />
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-12 md:col-span-6">
                                    <InputText
                                        type="text"
                                        label="Name"
                                        placeholder=""
                                        modelValue={formik.values.name}
                                        disabled={false}
                                        autofocus={true}
                                        id="name"
                                        validation={
                                            formik.errors.name &&
                                            formik.touched.name && {
                                                $error: true,
                                                required: {
                                                    $invalid: true,
                                                    $message: formik.errors.name,
                                                },
                                            }
                                        }
                                        onUpdateModelValue={function (value: string): void {
                                            formik.setFieldValue("name", value);
                                        }}
                                    />
                                </div>
                                <div className="col-span-12 md:col-span-6">
                                    <InputText
                                        type="text"
                                        label="Name in Gujarati"
                                        placeholder=""
                                        modelValue={formik.values.namegujarati}
                                        disabled={false}
                                        autofocus={false}
                                        id="namegujarati"
                                        onUpdateModelValue={function (value: string): void {
                                            formik.setFieldValue("namegujarati", value);
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-12">
                                    <label className="form-label">Icon</label>
                                    <p className="text-xs m-0 mb-1" style={{ color: "#9ca3af" }}>
                                        PNG or SVG recommended, 24 x 24
                                    </p>
                                    <div className="flex items-center gap-3 mt-1">
                                        {iconPreview ? (
                                            <img src={iconPreview} alt="icon" style={{ width: "40px", height: "40px", objectFit: "contain", borderRadius: "6px", border: "1px solid #e5e7eb", padding: "4px" }} />
                                        ) : (
                                            <div style={{ width: "40px", height: "40px", borderRadius: "6px", border: "1px dashed #d1d5db", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                <BuildingLibraryIcon style={{ width: "20px", height: "20px", color: "#d1d5db" }} />
                                            </div>
                                        )}
                                        <Button type="button" label="Choose Icon" outlined onClick={() => iconInputRef.current?.click()} />
                                        <input ref={iconInputRef} type="file" accept="image/png,image/svg+xml" hidden onChange={handleIconChange} />
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="field mt-5 mb-4">
                                    {id ? (
                                        <Button
                                            label="Edit"
                                            className="prime-button btn-login w-max"
                                            type="submit"
                                            loading={submitting}
                                        ></Button>
                                    ) : (
                                        <Button
                                            label="ADD"
                                            className="prime-button btn-login w-max"
                                            type="submit"
                                            loading={submitting}
                                        ></Button>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </Card>
            </form >
        </>
    );
};

export default GovtDepartmentCreate;
